import React, { FunctionComponent, useRef, useState } from 'react';
import I18n from 'common/i18n';
import { mapPermissionScopeToTargetAudience, withGuidanceV2 } from 'common/core/approvals/index_new';
import {
  showToastNow,
  showToastOnPageReload,
  ToastType
} from 'common/components/ToastNotification/Toastmaster';
import type { GuidanceSummaryV2 } from 'common/types/approvals';
import { ForgeButton } from '@tylertech/forge-react';
import { GuidanceBooleanCheckSummary, GuidanceSummaryV2Helper } from 'common/core/approvals/Types';
import { ForgePopup } from '@tylertech/forge-react';
import { View } from 'common/types/view';

const toastI18nScope = 'shared.components.asset_action_bar.publication_action';
const buttonI18nScope = `${toastI18nScope}.draft.primary_action_text`;

interface SubmitForApprovalButtonProps {
  approvalsGuidance: GuidanceSummaryV2;
  disabled: boolean;
  disabledReason?: string | JSX.Element;
  view: View;
}

const SubmitForApprovalButton: FunctionComponent<SubmitForApprovalButtonProps> = ({
  approvalsGuidance,
  disabled,
  disabledReason,
  view
}: SubmitForApprovalButtonProps) => {
  const targetRef = useRef<any>();
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const approvalsHelper: GuidanceSummaryV2Helper = withGuidanceV2(approvalsGuidance);
  const willEnterApprovalQueueSummary: GuidanceBooleanCheckSummary =
    approvalsHelper.summarizeWillEnterApprovalQueue();

  const onClick = () => {
    setBusy(true);

    const targetAudience = mapPermissionScopeToTargetAudience(view.permissions?.scope);
    let redirectTo = `/d/${approvalsHelper.publishedUid()}`;
    let successToastContent = I18n.t('shared.site_chrome.access_manager.publish.success_toast');

    if (
      targetAudience &&
      willEnterApprovalQueueSummary.length &&
      willEnterApprovalQueueSummary[targetAudience]
    ) {
      redirectTo = window.location.pathname;
      successToastContent = I18n.t('submitted_asset_for_approval', { scope: toastI18nScope });
    }

    // Currently targetAudience could be undefined. We want it to error if it is.
    if (targetAudience) {
      approvalsHelper
        .submitForApproval(targetAudience)
        .then(() => {
          showToastOnPageReload({
            type: ToastType.SUCCESS,
            content: successToastContent
          });
          window.location.href = redirectTo;
        })
        .catch((err: any) => {
          console.error(err);
          showToastNow({
            type: ToastType.ERROR,
            content: I18n.t('unknown_error', { scope: toastI18nScope })
          });
        });
    } else {
      throw new Error(
        'SubmitForApprovalButton - Submit action initiated without a specified target audience'
      );
    }
  };

  let title = '';
  if (willEnterApprovalQueueSummary.public) {
    title = I18n.t('updating_public_asset_requires_approval', { scope: buttonI18nScope });
  } else if (willEnterApprovalQueueSummary.internal) {
    title = I18n.t('updating_internal_asset_requires_approval', { scope: buttonI18nScope });
  }

  const buttonTextKey = willEnterApprovalQueueSummary.length ? 'submit_for_approval' : 'previously_published';

  return (
    <div onMouseEnter={() => setFlyoutOpen(true)} onMouseLeave={() => setFlyoutOpen(false)}>
      <ForgeButton type="raised" onClick={onClick}>
        <button
          data-testid="aab-submit-for-approval"
          ref={targetRef}
          type="button"
          disabled={disabled || busy}
          title={title}
        >
          {I18n.t(buttonTextKey, { scope: buttonI18nScope })}
        </button>
      </ForgeButton>
      {disabledReason && (
        <ForgePopup
          targetElementRef={targetRef}
          open={flyoutOpen}
          onDismiss={() => {}}
          options={{ placement: 'bottom-end' }}
        >
          <div className="forge-typography--body2">
            <div className="publish-button-flyout-content">{disabledReason}</div>
          </div>
        </ForgePopup>
      )}
    </div>
  );
};

export default SubmitForApprovalButton;
